import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatchType } from '../column-filter/column-filter-text-with-match-options-component';
import { JhiEventManager } from '../core-utils';
import { Subject } from 'rxjs';
import { BreedingMethodSearchRequest } from '../breeding-method/model/breeding-method-search-request.model';
import { HttpResponse } from '@angular/common/http';
import { BreedingMethod } from '../breeding-method/model/breeding-method';
import { BreedingMethodService } from '../breeding-method/service/breeding-method.service';
import { BreedingMethodClassMethodEnum } from '../breeding-method/model/breeding-method-class.enum';
import { BreedingMethodTypeEnum } from '../breeding-method/model/breeding-method-type.model';
import { DropdownOptionData } from '../column-filter/column-filter-dropdown-option-data';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';
import { SubscriptionsManager } from '../util/subscriptions-manager';

@Component({
    selector: 'jhi-breeding-methods-select',
    templateUrl: './breeding-methods-select.component.html'
})
export class BreedingMethodsSelectComponent implements OnInit, OnDestroy {

    @ViewChild('select') ngSelect: NgSelectComponent;

    static readonly BREEDING_METHODS_PAGE_SIZE = 300;

    static readonly DERIVATIVE_METHOD_TYPES: BreedingMethodTypeEnum[] = [BreedingMethodTypeEnum.DERIVATIVE, BreedingMethodTypeEnum.MAINTENANCE];
    static readonly GENERATIVE_METHOD_TYPES: BreedingMethodTypeEnum[] = [BreedingMethodTypeEnum.GENERATIVE];

    @Input() value: any;
    @Output() valueChange = new EventEmitter<any>();
    @Input() breedingMethodFilterTypeValue = BreedingMethodFilterTypeEnum.DERIVATIVE_AND_MAINTENANCE_ONLY;
    @Output() breedingMethodFilterTypeValueChange = new EventEmitter<BreedingMethodFilterTypeEnum>();
    @Input() useFavoriteBreedingMethods = false;
    @Input() disabled = false;
    @Input() showFavoritesCheckbox = true;
    @Input() disableFavoritesCheckbox = false;
    @Input() showMethodTypeFilterSection = true;
    @Input() showGenerativeMethodTypesOption = false;
    @Input() showDerivativeMethodTypesOption = true;
    @Input() showAllMethodTypesOption = true;
    @Input() disableMethodTypeFilterSection = false;
    @Input() helpLink: string;
    @Input() nonBulkingOnly = false;
    @Input() selectWidth = '100%';
    @Input() customClass;
    @Input() appendTo: string = '';

    @Output() onMethodChanged = new EventEmitter<BreedingMethod>();
    @Output() blur = new EventEmitter<void>();
    @Output() keyDown = new EventEmitter<KeyboardEvent>();

    BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum;

    pageIterator = 0;
    selectedBreedingMethodId: string;
    selectedBreedingMethodFilterTypeValue: BreedingMethodFilterTypeEnum;
    selectedUseFavoriteBreedingMethods;
    breedingMethodsFilteredItemsCount;

    searchSubject = new Subject<string>();
    breedingMethodsMap: Map<string, BreedingMethod> = new Map<string, BreedingMethod>();

    items: DropdownOptionData[];

    subscriptionManager = new SubscriptionsManager();


    constructor(private breedingMethodService: BreedingMethodService,
                private eventManager: JhiEventManager) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.selectedUseFavoriteBreedingMethods = this.useFavoriteBreedingMethods;
        this.selectedBreedingMethodFilterTypeValue = this.breedingMethodFilterTypeValue;
        // The breeding methods are retrieved only when the dropdown is opened, so we have to manually set the initial selected item on first load.
        // Get the breeding method and add it to the initial data.
        this.selectedBreedingMethodId = this.value;
        if (this.value) {
            this.breedingMethodService.queryBreedingMethod(Number(this.value)).toPromise().then((breedingMethod) => {
                this.items = [{
                    id: String(breedingMethod.mid),
                    text: breedingMethod.code + ' - ' + breedingMethod.name
                }];
                if (breedingMethod.type === BreedingMethodTypeEnum.GENERATIVE.toString()) {
                    // If the initial breeding method type is generative, select the Generative filter option by default
                    this.selectedBreedingMethodFilterTypeValue = BreedingMethodFilterTypeEnum.GENERATIVE_ONLY;
                } else {
                    // If the initial breeding method type is derivative or maintenance, select the Derivative filter option by default
                    this.selectedBreedingMethodFilterTypeValue = BreedingMethodFilterTypeEnum.DERIVATIVE_AND_MAINTENANCE_ONLY;
                }
                this.breedingMethodsMap.set(String(breedingMethod.mid), breedingMethod);
                this.onMethodChanged.emit(breedingMethod);
            });
        }

        if (this.showFavoritesCheckbox && this.useFavoriteBreedingMethods) {
            // Favorites checkbox is enabled by default, but only valid if favorite items exist.
            // If no favorites are available, force the checkbox to unchecked.
            this.subscriptionManager.add = this.getBreedingMethods(this.pageIterator, '', true).subscribe((res) => {
                const methods = res.body;
                this.selectedUseFavoriteBreedingMethods = methods.length !== 0;
            });
        }

        this.subscriptionManager.add = this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((searchTerm) => {
            this.search(searchTerm);
        }
        );
    }

    onSearch(searchTerm: string) {
        this.searchSubject.next(searchTerm);
    }

    search(searchTerm: string): void {
        this.pageIterator = 0;
        this.items = [];
        this.subscriptionManager.add = this.getBreedingMethods(this.pageIterator, searchTerm, this.selectedUseFavoriteBreedingMethods).subscribe((res) => {
            const methods = res.body;
            this.items = this.items.concat(methods.map((method) => {
                return {
                    id: String(method.mid),
                    text: method.code + ' - ' + method.name
                };
            }));
        });
    }

    private getBreedingMethods(page: number, searchTerm = '', isFavoriteBreedingMethods: boolean) {

        const breedingMethodSearchRequest: BreedingMethodSearchRequest = new BreedingMethodSearchRequest();
        breedingMethodSearchRequest.nameFilter = {
            type: MatchType.STARTSWITH,
            value: searchTerm
        };

        if (this.selectedBreedingMethodFilterTypeValue === BreedingMethodFilterTypeEnum.DERIVATIVE_AND_MAINTENANCE_ONLY) {
            breedingMethodSearchRequest.methodTypes = BreedingMethodsSelectComponent.DERIVATIVE_METHOD_TYPES;
        } else if (this.selectedBreedingMethodFilterTypeValue === BreedingMethodFilterTypeEnum.GENERATIVE_ONLY) {
            breedingMethodSearchRequest.methodTypes = BreedingMethodsSelectComponent.GENERATIVE_METHOD_TYPES;
        }

        const pagination = {
            page,
            size: BreedingMethodsSelectComponent.BREEDING_METHODS_PAGE_SIZE
        };

        if (this.nonBulkingOnly) {
            breedingMethodSearchRequest.methodClassIds = [BreedingMethodClassMethodEnum.NON_BULKING_BREEDING_METHOD_CLASS];
        }

        return this.breedingMethodService.searchBreedingMethods(
            breedingMethodSearchRequest,
            isFavoriteBreedingMethods,
            pagination
        ).pipe(map((res: HttpResponse<BreedingMethod[]>) => {
            this.breedingMethodsFilteredItemsCount = res.headers.get('X-Total-Count');
            res.body.forEach((method) => {
                this.breedingMethodsMap.set(String(method.mid), method);
            });
            return res;
        }));
    }

    fetchMore(searchTerm: string): void {
        if (this.canLoadMore()) {
            this.pageIterator = this.pageIterator + 1;
            this.subscriptionManager.add = this.getBreedingMethods(this.pageIterator, searchTerm, this.selectedUseFavoriteBreedingMethods).subscribe((res) => {
                const methods = res.body;
                this.items = this.items.concat(methods.map((method) => {
                    return {
                        id: String(method.mid),
                        text: method.code + ' - ' + method.name
                    };
                }));
            });
        }
    }

    private canLoadMore(): boolean {
        return (this.pageIterator * BreedingMethodsSelectComponent.BREEDING_METHODS_PAGE_SIZE) < this.breedingMethodsFilteredItemsCount;
    }

    onValueChanged($event): void {
        this.pageIterator = 0;
        const selectedBreedingMethod = this.breedingMethodsMap.get(this.selectedBreedingMethodId);
        if (selectedBreedingMethod) {
            this.valueChange.emit(this.selectedBreedingMethodId);
            this.onMethodChanged.emit(selectedBreedingMethod);
        }
    }

    onBreedingMethodFilterTypeValueChanged(): void {
        this.pageIterator = 0;
        this.selectedBreedingMethodId = null;
        this.items = [];
        this.breedingMethodFilterTypeValueChange.emit(this.selectedBreedingMethodFilterTypeValue);
    }

    onBlur() {
        if (this.blur) {
            this.blur.emit();
        }

    }

    onKeyDown(event: KeyboardEvent) {
        if (this.keyDown) {
            this.keyDown.emit(event);
        }

    }

    focus(): void {
        setTimeout(() => {
            this.ngSelect.focus();
        });
    }

    open(): void {
        setTimeout(() => {
            this.ngSelect.open();
        });
    }

    clearSelected(): void {
        this.selectedBreedingMethodId = null;
        this.value = null;
        this.valueChange.emit(null);
    }
}

export enum BreedingMethodFilterTypeEnum {
    DERIVATIVE_AND_MAINTENANCE_ONLY = 1,
    ALL_METHODS = 2,
    GENERATIVE_ONLY = 3
}
